import { useTranslation } from 'react-i18next';
import { useHealth, useReadAll } from '../../api/queries.js';
import { useRelativeTime } from '../../lib/time.js';
import type { FeedParams } from '../../lib/feedUrl.js';

export interface Pager {
  hasMore: boolean;
  loading: boolean;
  loadMore: () => void;
}

/** End of a card layout: "load more" while pages remain, otherwise "all done"
 * (or still fetching); mark-all only on the unfiltered unread feed; last fetch time. */
export default function FeedFooter({
  itemCount,
  params,
  pager,
  fetchingFirstArticles,
  showAllDone = true,
}: {
  itemCount: number;
  params: FeedParams;
  pager?: Pager;
  fetchingFirstArticles: boolean;
  showAllDone?: boolean;
}) {
  const { t } = useTranslation();
  const readAll = useReadAll();
  const health = useHealth();
  const relTime = useRelativeTime();
  const filtered = Boolean(params.category || params.tag || params.topic);

  if (pager?.hasMore) {
    return (
      <div className="flex justify-center px-8 py-10">
        <button
          onClick={pager.loadMore}
          disabled={pager.loading}
          className="min-h-[44px] rounded-full border border-neutral-300 px-6 text-sm font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
        >
          {pager.loading ? t('common.loading') : t('feed.loadMore')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 px-8 py-14 text-center">
      {fetchingFirstArticles ? (
        <>
          <p className="animate-pulse text-2xl font-bold text-neutral-400">{t('feed.fetching')}</p>
          <p className="text-sm text-neutral-400">{t('feed.fetchingHint')}</p>
        </>
      ) : (
        showAllDone && <p className="text-2xl font-bold text-neutral-400">{t('feed.allDone')}</p>
      )}
      {/* read-all is global on the server — never offer it inside a filtered view */}
      {itemCount > 0 && params.filter === 'unread' && !filtered && (
        <button
          onClick={() => readAll.mutate(new Date().toISOString())}
          className="min-h-[44px] rounded-full bg-accent px-5 text-sm font-semibold text-white"
        >
          {t('feed.markAllRead')}
        </button>
      )}
      {health.data?.lastFetchRunAt && (
        <p className="text-xs text-neutral-400">
          {t('feed.updatedAgo', { time: relTime(health.data.lastFetchRunAt) })}
        </p>
      )}
    </div>
  );
}
